/**
 * AdminSubscriptions Page
 *
 * "Total visibility" control tower for Stripe subscription states and
 * Enterprise tier (silo_mode) assignments across all customers
 * (GitHub issue #166).
 *
 * Fetches `GET /api/admin/subscriptions` (`apps/workers/src/api/admin/subscriptions.ts`)
 * and renders a paginated, sortable, filterable table with:
 * - Customer name
 * - Plan
 * - Subscription status (TRIALING/ACTIVE/PAST_DUE/CANCELED/UNPAID)
 * - Expires at
 * - Enterprise tier (silo_mode)
 *
 * AC: "Given an admin user is logged in, when they navigate to
 * /admin/subscriptions, then they see a paginated list of all customers
 * with their current subscription status, plan name, expiration date, and
 * Enterprise tier (silo_mode) displayed."
 * AC: "Given a subscription is marked as Enterprise tier with
 * silo_mode='enterprise', when an admin filters the dashboard by
 * Enterprise tier, then only Enterprise subscriptions are listed."
 *
 * RBAC: mounted behind `AdminGuard` (see `App.tsx`) — only
 * `is_superuser` users can reach this route; the API endpoint itself
 * independently re-verifies superuser status server-side (never trusts
 * the client-side guard alone).
 *
 * ------------------------------------------------------------------
 * Migrated for "Unified API client with RTK Query for all frontend data
 * fetching": previously fetched via a hand-rolled `useCallback` +
 * `useEffect` + local `useState` (manual loading/error/data plumbing,
 * duplicated auth-token/base-URL wiring). Now:
 * - Data fetching: `useAdminSubscriptions()` (`@/lib/api/apiClient`) —
 *   a TanStack Query hook, Zod-validated response, shared cache/retry
 *   policy.
 * - Filter/sort/pagination state: `useApiStore` (`@/stores/apiStore`,
 *   Zustand) instead of five separate `useState` calls — the query key
 *   passed to `useAdminSubscriptions` is derived directly from the
 *   store, so changing a filter automatically refetches.
 * - Loading state: ShadCN `Skeleton` instead of a plain text string
 *   (Requirement #8).
 * - Error state: `sonner` `toast.error` + inline retry, instead of only
 *   an inline message (Requirement #8).
 *
 * Related: Issue #166, docs/v2/04-stripe-billing.md §3.3/§4
 *
 * ------------------------------------------------------------------
 * GitHub issue #501 ("E2E: User summary dashboard — paid user analytics,
 * per-user breakdown") is covered against *this* page in
 * `e2e/user-summary.spec.ts`, not a dedicated `/user-summary` route — no
 * "user summary"/per-user-drill-down concept (stat cards, expandable
 * user→jobs→candidates rows, Screened/Sourced tabs, resume download)
 * exists anywhere in this codebase to test. This is the real,
 * closest-existing "paid user" admin list page (a per-*subscription*
 * control tower, not per-*user* analytics). See that spec's doc-comment
 * and `docs/flows/11-user-summary.md` / `docs/flows/GATE-113-SIGNOFF.md`
 * for the full rescoping rationale.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useAdminSubscriptions,
  useAdminSubscriptionPlans,
  useAdminUnlinkedSubscriptions,
} from '@/lib/api/apiClient';
import { useApiStore } from '@/stores/apiStore';
import { Skeleton } from '@/components/ui/skeleton';
import { reportApiError } from '@/lib/api/apiClient';
import type {
  AdminSubscriptionSiloMode,
  AppSubscriptionStatus,
} from '@/lib/validation/subscriptions';

/**
 * `price_cents` -> a human-readable "$X,XXX.XX" string. `0` renders as
 * "$0.00" verbatim (the exact issue #197 symptom for a *paid* plan is a
 * literal `$0.00` shown next to a placeholder-shaped Stripe Price ID —
 * this formatter is deliberately dumb/literal so that symptom is visible,
 * not silently hidden by e.g. omitting cents on a whole-dollar amount).
 */
function formatPlanPriceCents(priceCents: number): string {
  return `$${(priceCents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

type SortField = 'customer_name' | 'plan' | 'subscription_status' | 'expires_at' | 'silo_mode';

const STATUS_BADGE_CLASSNAMES: Record<AppSubscriptionStatus, string> = {
  TRIALING: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  PAST_DUE: 'bg-yellow-100 text-yellow-800',
  CANCELED: 'bg-gray-100 text-gray-800',
  UNPAID: 'bg-red-100 text-red-800',
};

export default function AdminSubscriptions() {
  const navigate = useNavigate();

  const filters = useApiStore((state) => state.adminSubscriptionsFilters);
  const setFilters = useApiStore((state) => state.setAdminSubscriptionsFilters);
  const toggleSort = useApiStore((state) => state.toggleAdminSubscriptionsSort);

  const { data, isLoading, isError, error, refetch, isFetching } = useAdminSubscriptions(filters);

  // Requirement #8: fire a sonner error toast once per failed fetch (not
  // once per re-render) — same pattern as
  // `app/routes/account/Subscription.tsx` / `src/app/routes/Subscription.tsx`.
  useEffect(() => {
    if (isError) {
      reportApiError(error, 'Failed to load subscriptions');
    }
  }, [isError, error]);

  const toggleSortField = (field: SortField) => {
    toggleSort(field);
  };

  const sortIndicator = (field: SortField) =>
    filters.sortBy === field ? (filters.sortOrder === 'asc' ? ' ▲' : ' ▼') : '';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md">
          <div className="text-lg font-semibold text-destructive">Error Loading Subscriptions</div>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'Failed to fetch subscriptions'}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="mt-4 px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasMore = filters.offset + filters.limit < data.total_count;
  const hasPrev = filters.offset > 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold">Subscriptions</h1>
            <button
              onClick={() => navigate('/admin')}
              className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium"
            >
              Back to Dashboard
            </button>
          </div>

          {/* Filters (Requirement #6: filter by subscription status and Enterprise tier) */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-4">
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({
                  status: e.target.value as AppSubscriptionStatus | '',
                  offset: 0,
                });
              }}
              className="px-3 py-2 border border-border rounded-md text-sm"
            >
              <option value="">All Statuses</option>
              <option value="TRIALING">Trialing</option>
              <option value="ACTIVE">Active</option>
              <option value="PAST_DUE">Past Due</option>
              <option value="CANCELED">Canceled</option>
              <option value="UNPAID">Unpaid</option>
            </select>

            <select
              value={filters.siloMode}
              onChange={(e) => {
                setFilters({
                  siloMode: e.target.value as AdminSubscriptionSiloMode | '',
                  offset: 0,
                });
              }}
              className="px-3 py-2 border border-border rounded-md text-sm"
            >
              <option value="">All Tiers</option>
              <option value="SHARED">Standard (Shared)</option>
              <option value="ENTERPRISE">Enterprise (Silo)</option>
            </select>

            <div className="text-sm text-muted-foreground pt-2 lg:col-span-2">
              Showing {data.data.length} of {data.total_count} subscriptions
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {data.data.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No subscriptions found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto border border-border rounded-lg mb-4">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th
                      className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                      onClick={() => toggleSortField('customer_name')}
                    >
                      Customer{sortIndicator('customer_name')}
                    </th>
                    <th
                      className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                      onClick={() => toggleSortField('plan')}
                    >
                      Plan{sortIndicator('plan')}
                    </th>
                    <th
                      className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                      onClick={() => toggleSortField('subscription_status')}
                    >
                      Status{sortIndicator('subscription_status')}
                    </th>
                    <th
                      className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                      onClick={() => toggleSortField('expires_at')}
                    >
                      Expires At{sortIndicator('expires_at')}
                    </th>
                    <th
                      className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                      onClick={() => toggleSortField('silo_mode')}
                    >
                      Enterprise Tier{sortIndicator('silo_mode')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((subscription) => (
                    <tr
                      key={subscription.id}
                      data-testid="admin-subscription-row"
                      className="border-b border-border hover:bg-muted/50"
                    >
                      <td className="px-4 py-2">
                        {subscription.customerName ||
                          subscription.customerEmail ||
                          subscription.customerId}
                      </td>
                      <td className="px-4 py-2">{subscription.planName || '—'}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${STATUS_BADGE_CLASSNAMES[subscription.subscriptionStatus]}`}
                        >
                          {subscription.subscriptionStatus}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {subscription.expiresAt
                          ? new Date(subscription.expiresAt).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-muted">
                          {subscription.siloMode === 'ENTERPRISE' ? 'Enterprise' : 'Standard'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center">
              <button
                onClick={() => setFilters({ offset: Math.max(0, filters.offset - filters.limit) })}
                disabled={!hasPrev}
                className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>

              <div className="text-sm text-muted-foreground">
                Page {Math.floor(filters.offset / filters.limit) + 1}
              </div>

              <button
                onClick={() => setFilters({ offset: filters.offset + filters.limit })}
                disabled={!hasMore}
                className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </>
        )}

        <UnlinkedSubscriptionsSection />
        <BillingPlansSection />
        {/*
         * TODO (Data Integrity: "Reconcile Stripe ↔ DB — every paying
         * customer linked (stripe_subscription_id) + visible in admin",
         * issue #238/#601): the full Stripe-side reconciliation audit
         * (matched / email-mismatch / fully-orphaned — see
         * `src/core/services/stripeDbReconciliation.ts` and the new
         * `GET /api/admin/stripe-reconciliation` /
         * `POST /api/admin/stripe-reconciliation/backfill` endpoints,
         * `apps/workers/src/api/admin/stripeReconciliation.ts`) is
         * implemented and admin-RBAC-protected, but this page doesn't yet
         * render it as its own section (would need a new
         * `useStripeReconciliationReport()` RTK Query hook in
         * `@/lib/api/apiClient` + response types in `@herohire/types`,
         * mirroring `useAdminUnlinkedSubscriptions` above — left out here
         * to stay focused on this ticket's backend/API scope; the two
         * endpoints are already fully functional and directly callable by
         * an admin/ops tool in the meantime). The existing subscriptions
         * table above + "Unlinked Subscriptions" section already surface
         * every linked paying customer's billing status per-row, which is
         * this ticket's core "visible in admin" requirement — this TODO
         * is only about surfacing the *new, more thorough* audit report
         * (email-mismatch classification specifically) as its own widget.
         */}
      </main>
    </div>
  );
}

/**
 * "Unlinked Subscriptions" section — bug: "Stripe webhook — paying
 * customer has NO user record, subscription creation failing since Jun
 * 9" (Sentry PYTHON-FASTAPI-2N/2P, subscription
 * `sub_1TgNV6Lj957TeqndmPVByhfE`). `handleSubscriptionCreated`
 * (`apps/workers/src/api/webhooks/stripe.ts`) durably records a
 * `stripe_pending_linkages` row once every user-resolution retry is
 * exhausted, instead of the paid subscription silently vanishing into
 * Sentry logs. This section lists those rows so an admin can identify
 * the customer (via Stripe/the checkout email) and manually link or
 * create their account (this issue's "Action needed" #3).
 *
 * Demo (this issue): "As an admin, open the pending-linkage /
 * unlinked-subscriptions admin view after the failing webhook exhausts
 * retries -> Subscription sub_1TgNV6Lj957TeqndmPVByhfE is listed as
 * unlinked with its checkout email so it can be manually linked to an
 * account."
 *
 * TODO (GitHub issue "P0: Pay-first customers orphaned — paid Stripe
 * subscription with no app account (Steve Smith, $1,250/mo)"): most new
 * pay-first customers no longer need this manual step at all —
 * `handleSubscriptionCreated` now auto-provisions an account inline (see
 * `apps/workers/src/lib/payFirstProvisioning.ts`). The backend mutation
 * for the rows that DO still need a human (e.g. Steve's, recorded before
 * that fix shipped) — `POST /api/admin/unlinked-subscriptions/:id/link`
 * (`apps/workers/src/api/admin/subscriptions.ts`'s
 * `linkPendingStripeSubscription`) — is implemented and tested
 * (`apps/workers/src/__tests__/api/admin/linkPendingStripeSubscription.test.ts`),
 * but this section doesn't yet render a "Link to user_id" action/button
 * for it — out of scope for this issue's edit budget (backend +
 * regression tests were the priority); a natural follow-up.
 */
function UnlinkedSubscriptionsSection() {
  const { data, isLoading, isError, error } = useAdminUnlinkedSubscriptions();

  return (
    <section className="mt-10">
      <h2 className="text-xl font-bold mb-4">Unlinked Subscriptions</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Paying Stripe customers whose subscription webhook could not be linked to any user
        account. Manually link or create the account so they get what they paid for.
      </p>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {isError && !isLoading && (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load unlinked subscriptions'}
        </p>
      )}

      {!isLoading && !isError && data && data.data.length === 0 && (
        <p className="text-sm text-muted-foreground">No unlinked subscriptions — billing is fully reconciled.</p>
      )}

      {!isLoading && !isError && data && data.data.length > 0 && (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Stripe Subscription</th>
                <th className="px-4 py-2 text-left font-semibold">Checkout Email</th>
                <th className="px-4 py-2 text-left font-semibold">Status</th>
                <th className="px-4 py-2 text-left font-semibold">Attempts</th>
                <th className="px-4 py-2 text-left font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((linkage) => (
                <tr
                  key={linkage.id}
                  data-testid="admin-unlinked-subscription-row"
                  className="border-b border-border hover:bg-muted/50"
                >
                  <td className="px-4 py-2 font-mono text-xs">{linkage.stripeSubscriptionId}</td>
                  <td className="px-4 py-2">{linkage.checkoutEmail || '—'}</td>
                  <td className="px-4 py-2">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      {linkage.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">{linkage.attempts}</td>
                  <td className="px-4 py-2 text-xs">
                    {new Date(linkage.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * "Billing Plans" catalog section — GitHub issue #197 ("subscription_plans
 * has placeholder Stripe price IDs -> every paying customer provisioned
 * on FREE Basic plan"). Distinct from the per-customer subscriptions
 * table above (which only ever showed a plan's display name): this
 * renders the `subscription_plans` catalog itself, so an admin can
 * directly verify each paid plan has a real Stripe Price ID (`price_1...`)
 * and a non-zero monthly price — not the pre-fix `price_id_basic`/$0.00
 * placeholders.
 *
 * Demo (issue #197): "As an admin, view the subscription plans / billing
 * config after the fix -> Each paid plan shows a real Stripe price ID
 * (price_1...) and a non-zero monthly price (e.g. $1,250 for Hero
 * Monthly), not $0 placeholders."
 */
function BillingPlansSection() {
  const { data, isLoading, isError, error } = useAdminSubscriptionPlans();

  return (
    <section className="mt-10">
      <h2 className="text-xl font-bold mb-4">Billing Plans</h2>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {isError && !isLoading && (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load billing plans'}
        </p>
      )}

      {!isLoading && !isError && data && (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Plan</th>
                <th className="px-4 py-2 text-left font-semibold">Stripe Price ID</th>
                <th className="px-4 py-2 text-left font-semibold">Monthly Price</th>
                <th className="px-4 py-2 text-left font-semibold">Jobs Limit</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((plan) => (
                <tr
                  key={plan.id}
                  data-testid="admin-billing-plan-row"
                  className="border-b border-border hover:bg-muted/50"
                >
                  <td className="px-4 py-2 font-medium">{plan.name}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {plan.stripePriceId}
                    {plan.isPlaceholderPriceId && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        Placeholder — needs a real Stripe Price ID
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">{formatPlanPriceCents(plan.priceCents)}</td>
                  <td className="px-4 py-2">{plan.jobsLimit === null ? 'Unlimited' : plan.jobsLimit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
